import { action, observable } from 'mobx';
import { IGetAllProjectsObject } from 'cd-eft/models/Projects/projectsLibrary';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';
import { noSpecialChars } from '../../../utils/noSpecialChars';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { UserInfoController } from 'cd-eft/services/eftPrjAdminApi';

const libraryStore = stores.AppStore.projectsLibraryStore;

interface IProjectSummaryForm {
  projectTitle: string;
  projectStatusId: number;
  client: string;
  designedBy: string;
  jobClass: number;
}

export interface IProjectSummaryProps {
  project: IGetAllProjectsObject;
  updateCallback: () => void;
}

export class ProjectSummaryVM {
  props: IProjectSummaryProps;
  constructor(props: IProjectSummaryProps) {
    this.props = props;
    this.project = props.project;
  }
  @observable project = {} as IGetAllProjectsObject;
  @observable projectionInfo: string = '';
  @observable editProjectSummary: boolean = false;
  @observable diffOwnerEmail: string = '';
  @observable newOwnerName: string = '';

  // Form Validation model
  @observable formValidation = {
    formErrors: {
      projectTitle: '',
      projectStatusId: '',
      client: '',
      designedBy: '',
      jobClass: '',
    },
    projectTitleValid: false,
    projectStatusIdValid: false,
    clientValid: false,
    designedByValid: false,
    jobClassValid: false,
    formValid: false,
  };

  @observable projectSummaryForm: IProjectSummaryForm = {
    projectTitle: '',
    projectStatusId: 0,
    client: '',
    designedBy: '',
    jobClass: 0,
  };

  @action.bound toggleEditProjectSummary() {
    this.editProjectSummary = !this.editProjectSummary;
  }

  @action.bound async handleEdit() {
    this.toggleEditProjectSummary();
    this.projectSummaryForm = {
      projectTitle: this.project.projectTitle,
      projectStatusId: this.project.projectStatusId,
      client: this.project.client,
      designedBy: this.project.designedBy,
      jobClass: this.project.jobClass ? this.project.jobClass : 0,
    };
  }

  @action.bound async handleCancelEdits() {
    this.toggleEditProjectSummary();
  }

  @action.bound async getNewOwnerInfo() {
    try {
      const response = await UserInfoController.getByUserIdUsingGET(this.project.projectOwnerId);
      if (response && response.data && response.data.userName) {
        this.diffOwnerEmail = response.data.userName;
        this.newOwnerName = response.data.userName.split('@')[0].replace('.', ' ');
      }
    }
    catch (e) {
      console.error((e as Error).message);
    }
  }

  @action.bound async handleSave() {
    if (!this.formValidation.formValid) {
      for (const field in this.projectSummaryForm) {
        const value = this.projectSummaryForm[field];
        this.validateField(field, value);
      }
      // If invalid - return and halt execution
      if (!this.formValidation.formValid) {
        return;
      }
    }

    const postJSON = {
      clientName: this.projectSummaryForm.client,
      designBy: this.projectSummaryForm.designedBy,
      jobClass: this.projectSummaryForm.jobClass,
      projectName: this.projectSummaryForm.projectTitle,
      projectStatus: this.projectSummaryForm.projectStatusId,
      projectUuid: this.project.projectUuid,
    };

    MessageCenter.clearMessages();
    try {
      const response = await PRJAPI.EftProjectController.editProjectSummaryUsingPOST(postJSON);
      /* if (response.data.projectUuid)
        await PRJAPI.EftProjectController.getProjectByIdUsingGET(response.data.projectUuid);
        */
      if (response.data) {
        this.project.projectUuid = response.data.projectUuid || '';
        this.project.projectTitle = response.data.projectName || '';
        this.project.projectStatusId = response.data.projectStatus || 0;
        this.project.client = response.data.clientName || '';
        this.project.jobClass = response.data.jobClass;
        this.project.designedBy = response.data.designBy || '';
        const temp = libraryStore.projectStatuses.filter((e) => e.id === this.project.projectStatusId);
        this.project.projectStatus = temp[0].description;
      }
      this.toggleEditProjectSummary();
      this.props.updateCallback();
    } catch (error) {
      MessageCenter.addError((error as Error).message);
      return;
    }
  }

  @action.bound async handleInputChange(event) {
    const target = event.target;
    let value = target.value;
    const name = target.name;
    this.projectSummaryForm[name] = value;
    // if value is number keep it a number
    if (!isNaN(parseFloat(value)) && isFinite(value)) {
      value = +value;
    }
    this.validateField(name, value);
  }

  @action.bound errorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage.length === 0 ? '' : ' usa-input--error';
  }
  // form validation by field
  @action.bound validateField(fieldName, value) {
    if (value === undefined) {
      return;
    }
    // connects form field to validation table
    const keyValid = fieldName + 'Valid';
    let fieldValid = this.formValidation[keyValid];
    const fieldValidationErrors = this.formValidation.formErrors;
    switch (fieldName) {
      case 'projectTitle':
        // is required and no special chars
        fieldValid = value !== '' && noSpecialChars(value);
        fieldValidationErrors[fieldName] = fieldValid
          ? ''
          : 'Project Name is required and only numbers and letters are allowed';
        break;
      case 'projectStatusId':
        // is required and value should not be 0
        fieldValid = value !== '' && value !== 0;
        fieldValidationErrors[fieldName] = fieldValid ? '' : 'Project Status is required';
        break;
      case 'client':
        // is required and no special chars
        fieldValid = value !== '' && noSpecialChars(value);
        fieldValidationErrors[fieldName] = fieldValid
          ? ''
          : 'Client Name is required and only numbers and letters are allowed';
        break;
      case 'designedBy':
        // is required and no special chars
        fieldValid = value !== '';
        fieldValidationErrors[fieldName] = fieldValid
          ? ''
          : 'Designed By is required and only numbers and letters are allowed';
        break;
      case 'jobClass':
        // is not required and value can be 0
        fieldValid = true;
        break;
      default:
        break;
    }
    this.formValidation.formErrors = fieldValidationErrors;
    this.formValidation[keyValid] = fieldValid;
    this.validateForm();
  }

  // checks if there are any error msgs over entire form
  @action.bound validateForm() {
    let valid = true;
    for (const key in this.formValidation.formErrors) {
      if (!this.formValidation[key + 'Valid']) {
        valid = false;
      }
    }
    this.formValidation.formValid = valid;
  }
}
